/**
 * Leads page — shows every web-inbound lead as a scrollable list.
 *
 * Purpose (per Andy 2026-08-06): visibility on what's coming in from web
 * sources (Rivercity Finance, AutoLoanCalculator, CompareFirst), with
 * one-click broker reassignment. Not a triage inbox — the lead already
 * exists as a contact + deal in the pipeline.
 */
import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Inbox,
  Search,
  ExternalLink,
  Loader2,
  RefreshCcw,
  Filter,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { leadsApi, usersApi, type LeadListItem } from "@/lib/api"
import { formatCurrency } from "@shared/utils/currency"
import { formatDateTime } from "@shared/utils/date"

// ────────────────────────────────────────────────────────────────────
// Source display — pretty label + colour per known slug
// ────────────────────────────────────────────────────────────────────
function sourceLabel(slug: string): string {
  const known: Record<string, string> = {
    "rivercity-finance": "Rivercity Finance",
    autoloancalc: "AutoLoanCalculator",
    comparefirst: "CompareFirst",
    facebook: "Facebook",
  }
  return known[slug] ?? slug
}

function sourceColour(slug: string): string {
  // Tailwind class-names keyed to source. Falls back to grey for unknown.
  const map: Record<string, string> = {
    "rivercity-finance": "bg-blue-100 text-blue-800",
    autoloancalc: "bg-emerald-100 text-emerald-800",
    comparefirst: "bg-purple-100 text-purple-800",
    facebook: "bg-sky-100 text-sky-800",
  }
  return map[slug] ?? "bg-gray-100 text-gray-800"
}

// ────────────────────────────────────────────────────────────────────
// Relative time helper — "just now / 5m ago / 2h ago / 3d ago / date"
// ────────────────────────────────────────────────────────────────────
function relativeTime(unixSec: number): string {
  const nowSec = Date.now() / 1000
  const diff = nowSec - unixSec
  if (diff < 60) return "just now"
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
  return new Date(unixSec * 1000).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

// ────────────────────────────────────────────────────────────────────
// Contact name/business helper
// ────────────────────────────────────────────────────────────────────
function contactName(lead: LeadListItem): string {
  const parts = [lead.contactFirstName, lead.contactLastName].filter(Boolean)
  const name = parts.join(" ").trim()
  return name || lead.contactBusinessName || "(unnamed)"
}

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────
export default function LeadsPage() {
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  const [source, setSource] = useState<string>("")
  const [assignedTo, setAssignedTo] = useState<string>("")
  const [filter, setFilter] = useState<"active" | "all">("active")

  const listQ = useQuery({
    queryKey: ["leads", { q, source, assignedTo, filter }],
    queryFn: () =>
      leadsApi.list({
        q: q || undefined,
        source: source || undefined,
        assignedTo: assignedTo || undefined,
        filter,
        pageSize: 100,
      }),
    staleTime: 15_000,
  })

  const sourcesQ = useQuery({
    queryKey: ["leads-sources"],
    queryFn: () => leadsApi.sources(),
    staleTime: 60_000,
  })

  const brokersQ = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
    staleTime: 60_000,
  })

  const brokerOptions = useMemo(
    () =>
      (brokersQ.data?.users ?? [])
        .filter((u) => u.isActive)
        .map((u) => ({ id: u.id, name: u.fullName ?? "(unnamed)" })),
    [brokersQ.data]
  )

  const assign = useMutation({
    mutationFn: ({ id, ownerId }: { id: string; ownerId: string | null }) =>
      leadsApi.assign(id, ownerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] })
      qc.invalidateQueries({ queryKey: ["deals-pipeline"] })
    },
  })

  const leads = listQ.data?.leads ?? []
  const total = listQ.data?.total ?? 0

  return (
    <AppShell
      title="Leads"
      subtitle="Inbound leads from website forms and partner APIs"
    >
      <div className="space-y-4">
        {/* Filter bar */}
        <Card className="p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search name, email, phone, business…"
                className="pl-8"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-muted-foreground h-4 w-4" />
              <select
                className="border-input bg-background h-9 rounded-md border px-2 text-sm"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                <option value="">All sources</option>
                {sourcesQ.data?.sources.map((s) => (
                  <option key={s} value={s}>
                    {sourceLabel(s)}
                  </option>
                ))}
              </select>
              <select
                className="border-input bg-background h-9 rounded-md border px-2 text-sm"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
              >
                <option value="">All brokers</option>
                <option value="unassigned">Unassigned</option>
                {brokerOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <select
                className="border-input bg-background h-9 rounded-md border px-2 text-sm"
                value={filter}
                onChange={(e) => setFilter(e.target.value as "active" | "all")}
              >
                <option value="active">Active only</option>
                <option value="all">All history</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => listQ.refetch()}
                title="Refresh"
              >
                <RefreshCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="text-muted-foreground mt-2 text-xs">
            {listQ.isLoading
              ? "Loading…"
              : `${total} lead${total === 1 ? "" : "s"}`}
          </div>
        </Card>

        {/* Table */}
        <Card className="p-0 overflow-hidden">
          {listQ.isLoading ? (
            <div className="text-muted-foreground flex items-center gap-2 p-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading leads…
            </div>
          ) : leads.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center gap-2 p-12 text-center">
              <Inbox className="h-8 w-8 opacity-50" />
              <div className="text-sm">
                {q || source || assignedTo
                  ? "No leads match those filters."
                  : "No leads yet. Web forms will land here as they arrive."}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-muted-foreground text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Received</th>
                    <th className="px-3 py-2 text-left font-medium">Source</th>
                    <th className="px-3 py-2 text-left font-medium">Name</th>
                    <th className="px-3 py-2 text-left font-medium">Contact</th>
                    <th className="px-3 py-2 text-left font-medium">Loan</th>
                    <th className="px-3 py-2 text-left font-medium">Stage</th>
                    <th className="px-3 py-2 text-left font-medium">
                      Assigned to
                    </th>
                    <th className="px-3 py-2 text-right font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="border-t hover:bg-muted/20"
                    >
                      <td
                        className="px-3 py-2 text-xs"
                        title={formatDateTime(lead.receivedAt)}
                      >
                        <div className="font-medium">
                          {relativeTime(lead.receivedAt)}
                        </div>
                        {lead.reusedExistingDeal && (
                          <div className="text-muted-foreground text-[10px]">
                            (re-submission)
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          className={sourceColour(lead.source)}
                          variant="outline"
                        >
                          {sourceLabel(lead.source)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        {lead.dealId ? (
                          <Link
                            to={`/deals/${lead.dealId}`}
                            className="font-medium hover:underline"
                          >
                            {contactName(lead)}
                          </Link>
                        ) : (
                          <span className="font-medium">
                            {contactName(lead)}
                          </span>
                        )}
                        {lead.contactBusinessName &&
                          contactName(lead) !== lead.contactBusinessName && (
                            <div className="text-muted-foreground text-[11px]">
                              {lead.contactBusinessName}
                            </div>
                          )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {lead.contactMobile && <div>{lead.contactMobile}</div>}
                        {lead.contactEmail && <div>{lead.contactEmail}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {lead.dealNaf != null && (
                          <div className="font-medium">
                            {formatCurrency(lead.dealNaf)}
                          </div>
                        )}
                        {lead.dealFinanceCategory && (
                          <div className="text-muted-foreground text-[11px]">
                            {lead.dealFinanceCategory}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs">
                          {(lead.dealPipelineStage ?? "new_lead").replace(
                            /_/g,
                            " "
                          )}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          className="border-input bg-background h-8 rounded border px-2 text-xs"
                          value={lead.ownerId ?? ""}
                          onChange={(e) =>
                            assign.mutate({
                              id: lead.id,
                              ownerId: e.target.value || null,
                            })
                          }
                          disabled={assign.isPending}
                        >
                          <option value="">Unassigned</option>
                          {brokerOptions.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {lead.dealId && (
                          <Link
                            to={`/deals/${lead.dealId}`}
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                          >
                            View
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  )
}
